import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Mail, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useToast } from '../../hooks/use-toast';
import { authService } from '../../services/auth';
import { useAuthStore } from '../../stores/authStore';

export function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { checkAuth } = useAuthStore();

  const [status, setStatus] = useState<'verifying' | 'success' | 'waitlist' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isResending, setIsResending] = useState(false);

  // Ref to prevent duplicate verification calls (React StrictMode runs effects twice in dev)
  const verificationAttempted = useRef(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid verification link. No token provided.');
      return;
    }

    // Prevent duplicate calls in development (React StrictMode)
    if (verificationAttempted.current) {
      return;
    }
    verificationAttempted.current = true;

    const verifyEmailToken = async () => {
      try {
        // Verify email and get auth tokens
        const authResponse = await authService.verifyEmail(token);

        // Check if user is on waitlist (explicit waitlist flag from backend)
        if ((authResponse as any).waitlist === true) {
          console.log('User on waitlist, showing waitlist screen');
          setStatus('waitlist');
          toast({
            title: 'Email verified!',
            description: "You're on the waitlist. We'll notify you when approved.",
          });
          return;
        }

        // The tokens are already stored by authService.verifyEmail in localStorage
        // Now update the auth store to sync the authentication state
        await checkAuth();

        // Set success status briefly
        setStatus('success');

        toast({
          title: 'Email verified!',
          description: 'Welcome! Redirecting to dashboard...',
        });

        // Short delay to let auth store update propagate
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 500);

      } catch (error) {
        setStatus('error');
        const message = error instanceof Error ? error.message : 'Verification failed';
        setErrorMessage(message);

        toast({
          title: 'Verification failed',
          description: message,
          variant: 'destructive',
        });
      }
    };

    verifyEmailToken();
  }, [token, navigate, toast, checkAuth]);

  const handleResendVerification = async () => {
    // Extract email from error message if available, or prompt user
    const email = searchParams.get('email');

    if (!email) {
      toast({
        title: 'Email required',
        description: 'Please go back to the signup page to resend verification.',
        variant: 'destructive',
      });
      navigate('/signup');
      return;
    }

    setIsResending(true);
    try {
      await authService.resendVerification(email);
      toast({
        title: 'Verification email sent',
        description: 'Please check your inbox for the new verification link.',
      });
      navigate(`/verification-sent?email=${encodeURIComponent(email)}`);
    } catch (error) {
      toast({
        title: 'Failed to resend',
        description: error instanceof Error ? error.message : 'Could not resend verification email',
        variant: 'destructive',
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4">
            {status === 'verifying' && (
              <Loader2 className="h-16 w-16 text-primary animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle2 className="h-16 w-16 text-green-600" />
            )}
            {status === 'waitlist' && (
              <Clock className="h-16 w-16 text-amber-500" />
            )}
            {status === 'error' && (
              <XCircle className="h-16 w-16 text-destructive" />
            )}
          </div>

          <CardTitle className="text-2xl font-bold">
            {status === 'verifying' && 'Verifying your email...'}
            {status === 'success' && 'Email verified!'}
            {status === 'waitlist' && 'Email verified!'}
            {status === 'error' && 'Verification failed'}
          </CardTitle>

          <CardDescription>
            {status === 'verifying' && 'Please wait while we verify your email address.'}
            {status === 'success' && 'You can now log in.'}
            {status === 'waitlist' && 'Thank you for your interest.'}
            {status === 'error' && 'We could not verify your email address.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {status === 'success' && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Your email has been successfully verified! Logging you in and redirecting to dashboard...
              </AlertDescription>
            </Alert>
          )}

          {status === 'waitlist' && (
            <>
              <Alert className="border-amber-200 bg-amber-50">
                <Clock className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  Your email has been verified! You're now on our waitlist. There are limited seats right now. We'll send you an email when your account is approved.
                </AlertDescription>
              </Alert>

              <div className="text-center space-y-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  Thank you for your interest!
                </p>
                <Button
                  onClick={() => navigate('/')}
                  className="w-full"
                >
                  Back to Homepage
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>

              <div className="space-y-2">
                <p className="text-sm text-muted-foreground text-center">
                  Your verification link may have expired or is invalid.
                </p>

                <Button
                  onClick={handleResendVerification}
                  disabled={isResending}
                  className="w-full"
                  variant="outline"
                >
                  {isResending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resending...
                    </>
                  ) : (
                    <>
                      <Mail className="mr-2 h-4 w-4" />
                      Resend Verification Email
                    </>
                  )}
                </Button>
              </div>
            </>
          )}

          {status === 'success' && (
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              Go to Dashboard
            </Button>
          )}
        </CardContent>

        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            Already verified?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}

export default VerifyEmail;
